import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button } from '@mui/material'
import * as Sentry from '@sentry/nextjs'

import OndemandVideoIcon from '@mui/icons-material/OndemandVideo'
import DownloadVideoModal from './DownloadVideoModal'
import PaymentModal from './PaymentModal'
import ThankYouModal from './ThankYouModal'
import RenderedModal from './RenderedModal'
import AlertDefaultIntroModal from './AlertDefaultIntroModal'

const DOWNLOAD_MODAL_STRING = 'download'
const PAYMENT_MODAL_STRING = 'payment'
const THANK_YOU_MODAL_STRING = 'thank-you'
const RENDERED_MODAL_STRING = 'rendered'

export default function DownloadVideoButton({
  intro, initiallyOpened, currentFormValues, alertTextChanged,
}) {
  const [openModal, setOpenModal] = useState(null)
  const [isDefaultIntroAlertOpen, setIsDefaultIntroAlertOpen] = useState(false)

  const isDownloadModalOpen = openModal === DOWNLOAD_MODAL_STRING
  const isPaymentModalOpen = openModal === PAYMENT_MODAL_STRING
  const isThankYouModalOpen = openModal === THANK_YOU_MODAL_STRING
  const isRenderedModalOpen = openModal === RENDERED_MODAL_STRING

  const onDownloadVideoClick = useCallback(() => {
    const isTextChanged = (
      currentFormValues.title1 !== intro.title1
      || currentFormValues.title2 !== intro.title2
    )
    if (isTextChanged) {
      alertTextChanged()
      return
    }

    if (intro.title1?.trim() === 'Breaking' && intro.title2?.trim() === 'Bad') {
      setIsDefaultIntroAlertOpen(true)
      return
    }

    if (intro.status === 'created') {
      setOpenModal(DOWNLOAD_MODAL_STRING)
      return
    }

    if (intro.status === 'paid') {
      setOpenModal(THANK_YOU_MODAL_STRING)
      return
    }

    if (intro.status === 'rendered') {
      setOpenModal(RENDERED_MODAL_STRING)
    }
  }, [intro, currentFormValues, alertTextChanged])

  useEffect(() => {
    if (initiallyOpened) {
      onDownloadVideoClick()
    }
  }, [])

  return (
    <>
      <Button
        variant="contained"
        onClick={onDownloadVideoClick}
        startIcon={<OndemandVideoIcon />}
        size="large"
      >
        Download MP4 Video
      </Button>
      <DownloadVideoModal
        open={isDownloadModalOpen}
        onClose={() => setOpenModal(null)}
        onConfirm={() => setOpenModal(PAYMENT_MODAL_STRING)}
      />
      <PaymentModal
        code={intro.code}
        open={isPaymentModalOpen}
        onClose={() => setOpenModal(null)}
        onPaymentComplete={(payload) => {
          Sentry.setUser({ email: payload.email })
          setOpenModal(THANK_YOU_MODAL_STRING)

          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'purchase',
              value: payload.finalAmount,
              currency: payload.currency,
            })
          }
        }}
      />
      <ThankYouModal
        open={isThankYouModalOpen}
        onClose={() => setOpenModal(null)}
      />
      <RenderedModal
        intro={intro}
        open={isRenderedModalOpen}
        onClose={() => setOpenModal(null)}
      />
      <AlertDefaultIntroModal
        open={isDefaultIntroAlertOpen}
        onClose={() => setIsDefaultIntroAlertOpen(false)}
        onConfirm={() => setIsDefaultIntroAlertOpen(false)}
      />
    </>
  )
}

DownloadVideoButton.propTypes = {
  intro: PropTypes.object,
  currentFormValues: PropTypes.object,
  initiallyOpened: PropTypes.bool,
  alertTextChanged: PropTypes.func,
}
